import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js/mobile";
import { translateWorkerNote } from "./workerNote";
import { probableEmail, type ProbableEmail } from "./probableEmail";
import {
  browserStorage,
  skipProbableWarning,
  warnBeforeProbable,
} from "./probableWarning";
import {
  Check,
  Flag,
  Linkedin,
  Loader2,
  Mail,
  Phone,
  Smartphone,
  Users,
  UserSearch,
} from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import {
  type Identifier,
  useGetIdentity,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  CompanyTeamLookup,
  ContactLookup,
  ContactRequest,
  CreditBalance,
  DecisionRole,
  Project,
  ProjectCompanyLink,
  ProjectContactLink,
} from "../types";
import { CompanyPhone } from "./CompanyPhone";
import { CompanySite } from "./CompanySite";
import { Fold } from "./Fold";
import { WatchCompanies } from "./ProjectWatch";
import { scrollToAnchor } from "./scrollToAnchor";
import { type Prices, useCreditPrices } from "./creditPrices";
import {
  freshFind,
  joinedFromTeam,
  leftTeam,
  sortContactsToReach,
  splitTeam,
  teamMemberIds,
} from "./contactOrder";
import { LockNote } from "./Teaser";
import { useInvited } from "./useInvited";
import { formatDay, LOW_CONFIDENCE } from "./projectUtils";
import { HelpTip } from "./HelpTip";
import { ContactRelation } from "./ContactRelation";
import { useProjectDeal } from "./DealFollowUp";
import { SentPrompt } from "./SentPrompt";

const DECISION_ROLES: DecisionRole[] = [
  "prescribes",
  "validates",
  "buys",
  "applies",
];

/** Contacts de la fiche : recherchés à la demande, chacun avec la source de ses données. */
/** Dernière demande de recherche, solde et lancement d'une recherche, partagés par le bloc et la barre d'actions. */

// Phrase du worker quand la fiche reste sans société (`radar contacts`, `find_companies`).
const NO_COMPANY_NOTE = "Aucune société connue pour ce projet";
export const useContactSearch = (project: Project) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const { data: requests, refetch } = useGetList<ContactRequest>(
    "contact_requests",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const { data: identity } = useGetIdentity();
  const { data: balances, refetch: refetchBalance } = useGetList<CreditBalance>(
    "credit_balances",
    { filter: { id: identity?.id }, pagination: { page: 1, perPage: 1 } },
    { enabled: !!identity },
  );
  const request = requests?.[0];
  const pending = request?.status === "pending";
  // La base refuse de toute façon une demande sans crédits ; ici on l'annonce avant le clic.
  const credit = balances?.[0];
  const available = credit ? credit.balance - credit.reserved : null;
  const short = credit != null && available! < credit.contacts_price;

  // La demande est enregistrée ; la recherche elle-même est faite par le worker.
  const search = async () => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc(
      "request_project_contacts",
      { project: project.id },
    );
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.contacts.requested", { type: "info" });
    refetch();
    refetchBalance();
  };
  return {
    request,
    pending,
    busy,
    credit,
    available,
    short,
    search,
    refetch,
    refetchBalance,
  };
};

/** Lance la recherche de contacts de la fiche, ou la relance quand des personnes sont déjà connues. */
export const ContactSearchButton = ({
  project,
  known,
  className,
  size,
}: {
  project: Project;
  known: boolean;
  className?: string;
  size?: "sm";
}) => {
  const translate = useTranslate();
  const { pending, busy, short, search } = useContactSearch(project);
  const prices = useCreditPrices();
  const searching = busy || pending;
  return (
    <Button
      className={className}
      variant="outline"
      size={size}
      disabled={searching || short}
      title={
        short
          ? translate("resources.projects.actions.credits_short")
          : undefined
      }
      onClick={search}
    >
      {searching ? <Loader2 className="animate-spin" /> : <UserSearch />}
      <span className="truncate">
        {translate(
          `resources.projects.contacts.${pending ? "pending" : known ? "search_again" : "search"}`,
          { smart_count: prices?.email ?? 1 },
        )}
      </span>
    </Button>
  );
};

/** Contacts affichés d'emblée ; les suivants derrière « Voir les N autres ». */
// Personnes visibles avant « voir les autres » : la chaîne de décision, au-dessus, les nomme déjà toutes.
const SHOWN_CONTACTS = 8;
const SHOWN_CONTACTS_MOBILE = 3;
// Sociétés dont on peut chercher l'équipe, visibles avant « voir les autres ».
const SHOWN_TEAMS = 5;

export const ProjectContacts = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  // Sur téléphone, la recherche se lance ici, à côté du solde et des personnes trouvées.
  const isMobile = useIsMobile();
  const invited = useInvited();
  const { deal } = useProjectDeal(project);
  const [reached, setReached] = useState<{
    contact: { id: number; name: string };
    kind: "email" | "linkedin";
  } | null>(null);
  const {
    request,
    pending,
    credit,
    available,
    short,
    refetch,
    refetchBalance,
  } = useContactSearch(project);
  const {
    data: contacts,
    isPending,
    refetch: refetchContacts,
  } = useGetList<ProjectContactLink>("project_contact_links", {
    filter: { project_id: project.id },
    // L'équipe d'une société ajoute jusqu'à 150 personnes (Mota-Engil México : 145, le 22/10/2026).
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "ASC" },
  });
  const { data: lookups, refetch: refetchLookups } = useGetList<ContactLookup>(
    "contact_lookups",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const prices = useCreditPrices();
  const [teamQuery, setTeamQuery] = useState("");
  // À la clôture d'une recherche d'équipe : les personnes ajoutées et le solde sont relus.
  const onTeamDone = useCallback(() => {
    refetchContacts();
    refetchBalance();
  }, [refetchContacts, refetchBalance]);
  const lookupPending = !!lookups?.some((l) => l.status === "pending");
  const wasLookupPending = useRef(false);
  useEffect(() => {
    if (wasLookupPending.current && !lookupPending) {
      refetchContacts();
      refetchBalance();
    }
    wasLookupPending.current = lookupPending;
    if (!lookupPending) return;
    const timer = setInterval(() => refetchLookups(), 5000);
    return () => clearInterval(timer);
  }, [lookupPending, refetchLookups, refetchContacts, refetchBalance]);
  // Pendant la recherche, la demande est relue toutes les 5 secondes ; à sa clôture, les contacts
  // trouvés et le solde sont relus aussitôt, sans recharger la page.
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) {
      refetchContacts();
      refetchBalance();
    }
    wasPending.current = pending;
    if (!pending) return;
    const timer = setInterval(() => refetch(), 5000);
    return () => clearInterval(timer);
  }, [pending, refetch, refetchContacts, refetchBalance]);
  // Une personne de l'équipe qui reçoit un e-mail ou un téléphone passe dans les contacts de la fiche : sa ligne reste
  // dans le bloc des équipes jusqu'à la fin de la visite, avec un lien qui mène à sa nouvelle place et la surligne.
  const previousTeam = useRef<Set<ProjectContactLink["contact_id"]> | null>(
    null,
  );
  const [moved, setMoved] = useState<ProjectContactLink["contact_id"][]>([]);
  const [highlighted, setHighlighted] = useState<
    ProjectContactLink["contact_id"] | null
  >(null);
  useEffect(() => {
    if (!contacts) return;
    const gone = leftTeam(previousTeam.current, contacts);
    if (gone.length)
      setMoved((current) => [
        ...current,
        ...gone.map((c) => c.contact_id).filter((id) => !current.includes(id)),
      ]);
    previousTeam.current = teamMemberIds(contacts);
  }, [contacts]);
  useEffect(() => {
    if (highlighted == null) return;
    const timer = setTimeout(() => setHighlighted(null), 2500);
    return () => clearTimeout(timer);
  }, [highlighted]);
  // Au-delà des premiers contacts (déjà triés, joignables d'abord), la liste est repliée : une fiche fouillée en
  // compte plus de vingt.
  const [showAll, setShowAll] = useState(false);
  const shown = isMobile ? SHOWN_CONTACTS_MOBILE : SHOWN_CONTACTS;
  // Fiche suivante (J / K) : liste de nouveau repliée.
  useEffect(() => setShowAll(false), [project.id]);
  const showContact = (id: ProjectContactLink["contact_id"]) => {
    const hidden =
      !showAll &&
      sortContactsToReach(splitTeam(contacts ?? [], teamQuery).others)
        .slice(shown)
        .some((contact) => contact.contact_id === id);
    if (hidden) {
      setShowAll(true);
      // La carte n'existe qu'au rendu suivant.
      setTimeout(() => scrollToAnchor(`contact-${id}`), 50);
    } else scrollToAnchor(`contact-${id}`);
    setHighlighted(id);
  };
  if (isPending) return null;
  const {
    others,
    groups,
    count: teamCount,
  } = splitTeam(contacts ?? [], teamQuery);
  // Recherches par personne : seulement sur les contacts de son côté (la base le vérifie aussi).
  const side = credit?.partner && !credit.administrator ? credit.id : null;

  return (
    <Card data-anchor="contacts" data-tour="contacts">
      <CardContent className="flex flex-col gap-4">
        {/* Sur ordinateur, la recherche se lance depuis le titre de la section ; la barre du bas garde l'affaire. */}
        <div className="flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-1 text-sm font-bold">
            {translate("resources.projects.contacts.title")}
            <HelpTip topic="emails" />
          </h3>
          {!isMobile && !invited && (
            <ContactSearchButton
              project={project}
              known={!!contacts?.length}
              size="sm"
              className="min-w-0 shrink"
            />
          )}
        </div>
        {invited && (
          <LockNote className="text-xs">
            {translate("resources.projects.teaser.contacts")}
          </LockNote>
        )}
        {pending && (
          <p className="text-xs text-muted-foreground">
            {translate("resources.projects.contacts.pending_help")}
          </p>
        )}
        {/* Un invité ne lance pas de recherche : son solde ne le concerne pas ici. */}
        {credit && !invited && (
          <Link
            to="/credit_balances"
            className={`self-start text-sm font-semibold tabular-nums no-underline hover:underline ${short ? "text-inferred-ink" : ""}`}
          >
            {translate("resources.projects.credits.on_project", {
              smart_count: available ?? 0,
              count: (available ?? 0).toLocaleString(locale),
            })}
          </Link>
        )}
        {credit && !invited && (
          <p
            className={`text-xs ${short ? "text-inferred-ink" : "text-muted-foreground"}`}
          >
            {translate(
              `resources.projects.contacts.${short ? "credits_short" : "credits"}`,
              { price: credit.contacts_price, available },
            )}
          </p>
        )}
        {isMobile && !invited && (
          <ContactSearchButton
            project={project}
            known={!!contacts?.length}
            className="w-full"
          />
        )}
        {request && !pending && (
          <p className="text-xs text-muted-foreground">
            {translate(
              `resources.projects.contacts.${request.status === "error" ? "last_error" : "last_search"}`,
              {
                date: formatDay(request.processed_at, locale),
                smart_count: request.found ?? 0,
              },
            )}
            {request.note && ` ${translateWorkerNote(request.note, translate)}`}
          </p>
        )}
        {/* Aucune société, même après la recherche sur le web : la suite utile est l'alerte « société arrivée ». */}
        {request &&
          !pending &&
          !invited &&
          request.note?.includes(NO_COMPANY_NOTE) && (
            <WatchCompanies project={project} roles={[]} hideMissing />
          )}
        {!!contacts?.length && (
          <DecisionChain contacts={contacts} onShow={showContact} />
        )}
        {!others.length ? (
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.contacts.empty")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {sortContactsToReach(others)
              .slice(0, showAll ? undefined : shown)
              .map((contact) => (
                <ContactItem
                  key={contact.id}
                  contact={contact}
                  onChange={refetchContacts}
                  lookups={lookups?.filter(
                    (l) => l.contact_id === contact.contact_id,
                  )}
                  available={available}
                  prices={prices}
                  country={project.country}
                  ownSide={(contact.partner_id ?? null) === side}
                  onReached={
                    // Un invité ne note pas d'échange : la base le refuse.
                    deal && !deal.archived_at && !invited
                      ? (kind) =>
                          setReached({
                            contact: {
                              id: Number(contact.contact_id),
                              name: contactName(contact),
                            },
                            kind,
                          })
                      : undefined
                  }
                  highlighted={highlighted === contact.contact_id}
                  onRequested={() => {
                    refetchLookups();
                    refetchBalance();
                  }}
                />
              ))}
          </ul>
        )}
        {!showAll && others.length > shown && (
          <button
            type="button"
            className="w-fit rounded-[3px] text-sm font-medium underline underline-offset-2"
            onClick={() => setShowAll(true)}
          >
            {translate("resources.projects.contacts.show_more", {
              smart_count: others.length - shown,
            })}
          </button>
        )}
        {!invited && (
          <CompanyTeams
            project={project}
            available={available}
            prices={prices}
            onDone={onTeamDone}
          />
        )}
        {(teamCount > 0 || moved.length > 0) && (
          <Fold
            summary={translate("resources.projects.contacts.team.members", {
              smart_count: teamCount,
            })}
          >
            <p className="mt-2 text-xs text-muted-foreground">
              {translate("resources.projects.contacts.team.members_help")}
            </p>
            <Input
              className="mt-2 h-8 text-sm"
              value={teamQuery}
              onChange={(event) => setTeamQuery(event.target.value)}
              placeholder={translate("resources.projects.contacts.team.filter")}
            />
            {moved.map((id) => {
              const contact = others.find((c) => c.contact_id === id);
              if (!contact) return null;
              const joined = joinedFromTeam(contact);
              return (
                <p key={id} className="mt-3 text-xs">
                  {translate(
                    `resources.projects.contacts.team.moved_${joined?.field ?? "other"}`,
                    {
                      name: [contact.first_name, contact.last_name]
                        .filter(Boolean)
                        .join(" "),
                    },
                  )}{" "}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => showContact(id)}
                  >
                    {translate("resources.projects.contacts.team.see")}
                  </button>
                </p>
              );
            })}
            {groups.map((group) => (
              <div key={group.company} className="mt-3 flex flex-col gap-3">
                <h4 className="text-xs font-bold">
                  {group.company} · {group.members.length}
                </h4>
                <ul className="flex flex-col divide-y divide-border">
                  {group.members.map((contact) => (
                    <ContactItem
                      key={contact.id}
                      contact={contact}
                      onChange={refetchContacts}
                      lookups={lookups?.filter(
                        (l) => l.contact_id === contact.contact_id,
                      )}
                      available={available}
                      prices={prices}
                      country={project.country}
                      ownSide={(contact.partner_id ?? null) === side}
                      onReached={
                        deal && !deal.archived_at && !invited
                          ? (kind) =>
                              setReached({
                                contact: {
                                  id: Number(contact.contact_id),
                                  name: contactName(contact),
                                },
                                kind,
                              })
                          : undefined
                      }
                      inTeam
                      onRequested={() => {
                        refetchLookups();
                        refetchBalance();
                      }}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </Fold>
        )}
        {reached && deal && (
          <SentPrompt
            deal={deal}
            contact={reached.contact}
            kind={reached.kind}
            onClose={() => setReached(null)}
          />
        )}
      </CardContent>
    </Card>
  );
};

/** Équipe d'une société de la fiche (titulaire, maître d'ouvrage…) : personnes en poste par famille de fonctions
 * (achats, projets, ingénierie, chantier, direction), cherchées chez Airscale à la demande. La base vérifie le solde
 * et que la société est sur la fiche ; le worker cherche et débite si au moins une personne est ajoutée. */
const CompanyTeams = ({
  project,
  available,
  prices,
  onDone,
}: {
  project: Project;
  available: number | null;
  prices: Prices | null;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const [busy, setBusy] = useState<Identifier | null>(null);
  // Au-delà des premières sociétés, la liste est repliée, comme dans le bloc Sociétés.
  const [allTeams, setAllTeams] = useState(false);
  useEffect(() => setAllTeams(false), [project.id]);
  const { data: links } = useGetList<ProjectCompanyLink>(
    "project_company_links",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 100 },
    },
  );
  const { data: lookups, refetch } = useGetList<CompanyTeamLookup>(
    "company_team_lookups",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const running = !!lookups?.some((l) => l.status === "pending");
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) onDone();
    wasRunning.current = running;
    if (!running) return;
    const timer = setInterval(() => refetch(), 5000);
    return () => clearInterval(timer);
  }, [running, refetch, onDone]);
  // Une société par ligne, même si elle tient plusieurs rôles ; les bailleurs ne bâtissent pas l'ouvrage.
  const companies = [
    ...new Map(
      (links ?? [])
        .filter((link) => link.role !== "funder")
        .map((link) => [link.company_id, link]),
    ).values(),
  ];
  if (!companies.length) return null;
  const price = prices?.team ?? 0;
  const short = available != null && available < price;
  const ask = async (company: Identifier) => {
    setBusy(company);
    const { error } = await getSupabaseClient().rpc("request_company_team", {
      project: project.id,
      company,
    });
    setBusy(null);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.contacts.team.requested", { type: "info" });
    refetch();
  };
  return (
    <section className="flex flex-col gap-1 border-t border-border pt-3">
      <h4 className="text-sm font-medium">
        {translate("resources.projects.contacts.team.fold")}
      </h4>
      <p className="text-xs text-muted-foreground">
        {translate("resources.projects.contacts.team.help", {
          smart_count: price,
        })}
      </p>
      {short && (
        <p className="text-xs text-inferred-ink">
          {translate("resources.projects.contacts.team.short", {
            price,
            available,
          })}
        </p>
      )}
      <ul className="mt-1 flex flex-col gap-2">
        {companies.slice(0, allTeams ? undefined : SHOWN_TEAMS).map((link) => {
          const latest = lookups?.find((l) => l.company_id === link.company_id);
          const pending = latest?.status === "pending";
          // Équipe déjà trouvée : le résultat remplace le bouton (la même équipe serait reprise sans rien ajouter).
          const found = latest?.status === "done" && (latest.found ?? 0) > 0;
          return (
            <li key={link.company_id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">{link.company_name}</span>
                {!found && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={
                      !prices || pending || busy === link.company_id || short
                    }
                    onClick={() => ask(link.company_id)}
                  >
                    {pending || busy === link.company_id ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Users />
                    )}
                    {pending
                      ? translate("resources.projects.contacts.team.pending")
                      : translate("resources.projects.contacts.team.button", {
                          smart_count: price,
                        })}
                  </Button>
                )}
              </div>
              {latest && !pending && (
                <span className="text-xs text-muted-foreground">
                  {translate("resources.projects.contacts.team.result", {
                    date: formatDay(latest.processed_at, locale),
                    smart_count: latest.found ?? 0,
                  })}
                  {latest.note &&
                    ` ${translateWorkerNote(latest.note, translate)}`}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {!allTeams && companies.length > SHOWN_TEAMS && (
        <button
          type="button"
          className="mt-2 w-fit rounded-[3px] text-sm font-medium underline underline-offset-2"
          onClick={() => setAllTeams(true)}
        >
          {translate("resources.projects.companies_show_more", {
            smart_count: companies.length - SHOWN_TEAMS,
          })}
        </button>
      )}
    </section>
  );
};

const contactName = (contact: ProjectContactLink) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
  contact.company_name ||
  "";

/** Résumé de la chaîne : qui prescrit, valide, achète et applique sur ce projet. */
const DecisionChain = ({
  contacts,
  onShow,
}: {
  contacts: ProjectContactLink[];
  onShow: (id: ProjectContactLink["contact_id"]) => void;
}) => {
  const translate = useTranslate();
  const placed = contacts.filter(
    (contact) => contact.decision_role && !contact.funder_only,
  );
  return (
    <div className="flex flex-col gap-1 border-y border-border py-3">
      <h4 className="flex items-center gap-1 text-xs font-bold">
        {translate("resources.projects.contacts.decision.title")}
        <HelpTip topic="chain" />
      </h4>
      {!placed.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.contacts.decision.empty")}
        </p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          {DECISION_ROLES.map((role) => {
            const members = placed.filter((c) => c.decision_role === role);
            if (!members.length) return null;
            return (
              <div key={role} className="contents">
                <dt className="font-medium">
                  {translate(
                    `resources.projects.contacts.decision.roles.${role}`,
                  )}
                </dt>
                <dd>
                  {members.map((member, index) => (
                    <span key={member.id}>
                      {index > 0 && ", "}
                      <button
                        type="button"
                        onClick={() => onShow(member.contact_id)}
                        className="rounded-[3px] text-left underline decoration-border underline-offset-2 hover:decoration-foreground"
                      >
                        {contactName(member)}
                      </button>
                    </span>
                  ))}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
};

/** Contact d'un bailleur : il finance sans choisir les produits, il renseigne sur le projet. */
// La pastille suit le nom, pour qu'on le voie avant de contacter la personne ; l'explication remplace la chaîne.
const FunderBadge = () => {
  const translate = useTranslate();
  return (
    <Badge
      variant="outline"
      className="border-inferred text-inferred-ink"
      title={translate("resources.projects.contacts.funder.help")}
    >
      {translate("resources.projects.contacts.funder.label")}
    </Badge>
  );
};

const FunderNote = () => {
  const translate = useTranslate();
  return (
    <div className="text-xs text-muted-foreground mt-1">
      {translate("resources.projects.contacts.funder.help")}
    </div>
  );
};

/** Place d'un contact dans la chaîne : la proposition de l'outil, ou la correction d'un utilisateur. */
const DecisionPlace = ({
  contact,
  onChange,
}: {
  contact: ProjectContactLink;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const [saving, setSaving] = useState(false);
  // La base marque la valeur comme corrigée et efface la proposition ; seule cette colonne est modifiable.
  const save = async (value: string) => {
    setSaving(true);
    const { error } = await getSupabaseClient()
      .from("project_contacts")
      .update({ decision_role: value === "none" ? null : value })
      .eq("project_id", contact.project_id)
      .eq("contact_id", contact.contact_id);
    setSaving(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.contacts.decision.saved", { type: "info" });
    onChange();
  };
  const status =
    contact.decision_method === "manual"
      ? translate("resources.projects.contacts.decision.manual", {
          date: formatDay(contact.decision_at, locale),
        })
      : contact.decision_method === "inferred" && contact.decision_role
        ? translate("resources.projects.contacts.decision.inferred", {
            confidence: Math.round((contact.decision_confidence ?? 0) * 100),
          })
        : !contact.decision_at
          ? translate("resources.projects.contacts.decision.not_assessed")
          : null;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {translate("resources.projects.contacts.decision.label")}
        </span>
        <Select
          value={contact.decision_role ?? "none"}
          onValueChange={save}
          disabled={saving}
        >
          <SelectTrigger
            size="sm"
            className="h-7 w-auto text-xs"
            aria-label={translate(
              "resources.projects.contacts.decision.place_of",
              { name: contactName(contact) },
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...DECISION_ROLES, "none"].map((role) => (
              <SelectItem key={role} value={role}>
                {translate(
                  `resources.projects.contacts.decision.roles.${role}`,
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {status && (
          <span className="text-xs text-muted-foreground">{status}</span>
        )}
      </div>
      {contact.decision_method === "inferred" && contact.decision_reason && (
        <div className="text-xs text-muted-foreground">
          {contact.decision_reason}
        </div>
      )}
      {contact.decision_method !== "manual" && (
        <div className="text-xs text-muted-foreground">
          {translate("resources.projects.contacts.decision.help")}
        </div>
      )}
    </div>
  );
};

// Chiffres d'un numéro, pour reconnaître le même écrit autrement ; les 9 derniers suffisent (indicatif ou 0).
const digits = (number?: string | null) =>
  (number ?? "").replace(/\D/g, "").slice(-9);

/** Vrai si le numéro est un mobile (indicatif international, ou à défaut le pays de la fiche). */
const isMobile = (number: string, country?: string | null) => {
  const region =
    country && /^[A-Z]{2}$/.test(country)
      ? (country as CountryCode)
      : undefined;
  // Avec les métadonnées « mobile », seul un numéro de mobile est valide.
  return !!parsePhoneNumberFromString(number, region)?.isValid();
};

const LOOKUP_KINDS: ContactLookup["kind"][] = ["email", "linkedin", "mobile"];
// Ordre des boutons de recherche d'une personne, et leur icône.
const LOOKUP_BUTTONS: ContactLookup["kind"][] = ["email", "mobile", "linkedin"];
const LOOKUP_ICONS: Record<ContactLookup["kind"], typeof Mail> = {
  email: Mail,
  mobile: Smartphone,
  linkedin: Linkedin,
};

/** Recherches payantes pour une personne : e-mail, profil LinkedIn, mobile, proposées seulement si la donnée
 * manque. La base vérifie le solde et enregistre la demande ; le worker cherche et débite si c'est trouvé. */
const usePersonLookups = ({
  contact,
  lookups,
  available,
  prices,
  country,
  onRequested,
}: {
  contact: ProjectContactLink;
  lookups?: ContactLookup[];
  available: number | null;
  prices: Prices | null;
  country?: string | null;
  onRequested: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState<string | null>(null);
  const phones = (contact.phone_jsonb ?? []).map((p) => p.number);
  // Adresse probable : la recherche payante cherche alors une adresse vérifiée ou différente ; inutile si nos
  // fournisseurs ont déjà rendu la même.
  const probable = probableEmail(contact);
  const missing: Record<ContactLookup["kind"], boolean> = {
    email: !contact.email_jsonb?.length && !probable?.known_by_providers,
    linkedin: !contact.linkedin_url,
    // Pas de recherche de mobile si un mobile est déjà connu (lu, ou trouvé par un fournisseur).
    mobile:
      !contact.evidence.some(
        (e) => e.field === "phone" && e.method === "provider",
      ) && !phones.some((number) => isMobile(number, country)),
  };
  // Une recherche de profil est gratuite mais demande, comme la recherche de contacts, le prix d'un e-mail.
  const price = (kind: ContactLookup["kind"]) =>
    kind === "mobile" ? (prices?.mobile ?? 0) : (prices?.email ?? 0);
  const latest = (kind: ContactLookup["kind"]) =>
    lookups?.find((l) => l.kind === kind);
  const short = (kind: ContactLookup["kind"]) =>
    available != null && available < price(kind);
  const ask = async (kind: ContactLookup["kind"]) => {
    setBusy(kind);
    const { error } = await getSupabaseClient().rpc("request_contact_lookup", {
      project: contact.project_id,
      contact: contact.contact_id,
      lookup: kind,
    });
    setBusy(null);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.contacts.lookup.requested", { type: "info" });
    onRequested();
  };
  // Boutons bloqués faute de crédits : la raison est écrite sous les coordonnées, pas seulement au survol.
  const blocked = LOOKUP_KINDS.filter(
    (kind) =>
      missing[kind] && short(kind) && latest(kind)?.status !== "pending",
  );
  return {
    missing,
    probable,
    price,
    latest,
    short,
    ask,
    busy,
    blocked,
    ready: !!prices,
  };
};

type PersonLookupsState = ReturnType<typeof usePersonLookups>;

/** Bouton d'une coordonnée manquante, avec son prix ; le résultat d'une recherche close sans donnée à côté. */
const LookupButton = ({
  kind,
  search,
}: {
  kind: ContactLookup["kind"];
  search: PersonLookupsState;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const last = search.latest(kind);
  const running = last?.status === "pending";
  const price = search.price(kind);
  const Icon = LOOKUP_ICONS[kind];
  // Avec une adresse probable, la recherche d'e-mail cherche une adresse vérifiée.
  const label = kind === "email" && search.probable ? "email_verified" : kind;
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        title={translate(`resources.projects.contacts.lookup.${label}`, {
          smart_count: price,
        })}
        disabled={
          !search.ready || running || search.busy === kind || search.short(kind)
        }
        onClick={() => search.ask(kind)}
      >
        {running || search.busy === kind ? (
          <Loader2 className="animate-spin" />
        ) : (
          <Icon />
        )}
        {running
          ? translate("resources.projects.contacts.lookup.running", {
              kind: translate(
                `resources.projects.contacts.lookup.kinds.${kind}`,
              ),
            })
          : translate(`resources.projects.contacts.lookup.button.${label}`, {
              smart_count: price,
            })}
      </Button>
      {/* Résultat négatif sous la rangée des boutons. */}
      {last && !running && !last.found && last.note && (
        <span className="order-last basis-full text-xs text-muted-foreground">
          {translate("resources.projects.contacts.lookup.result", {
            kind: translate(`resources.projects.contacts.lookup.kinds.${kind}`),
            date: formatDay(last.processed_at, locale),
            note: translateWorkerNote(last.note, translate, {
              probable: kind === "email" && !!search.probable,
            }),
          })}
        </span>
      )}
    </>
  );
};

/** Ligne d'une coordonnée : son icône, puis la donnée ou le bouton qui la cherche. */
const CoordinateRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Mail;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-h-7">
    <Icon
      className="size-4 shrink-0 text-muted-foreground"
      aria-label={label}
    />
    {children}
  </div>
);

type ReportReason = "unrelated" | "left" | "email" | "phone" | "linkedin";

/** Signalement d'une erreur, avec son motif : personne sans rapport avec la fiche, partie de sa société, ou donnée
 * fausse. La base ne l'applique qu'au côté de l'auteur, limite à 20 par jour et prévient le responsable des données. */
const ReportContact = ({
  contact,
  email,
  phone,
  onDone,
}: {
  contact: ProjectContactLink;
  email?: string;
  phone?: string;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const values: Partial<Record<ReportReason, string>> = {
    email,
    phone,
    linkedin: contact.linkedin_url ?? undefined,
  };
  const reasons = (
    ["unrelated", "left", "email", "phone", "linkedin"] as ReportReason[]
  ).filter((r) => r === "unrelated" || r === "left" || values[r]);
  const close = () => {
    setOpen(false);
    setReason(null);
    setNote("");
  };
  const send = async () => {
    if (!reason) return;
    setSending(true);
    const { error } = await getSupabaseClient().rpc("report_contact", {
      contact: contact.contact_id,
      project: contact.project_id,
      reason,
      data_value: values[reason] ?? null,
      note: note || null,
    });
    setSending(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    close();
    notify(`resources.projects.contacts.report.done_${reason}`, {
      type: "info",
    });
    onDone();
  };
  if (!open) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 w-fit text-xs"
        onClick={() => setOpen(true)}
      >
        <Flag />
        {translate("resources.projects.contacts.report.action")}
      </Button>
    );
  }
  return (
    <div className="flex max-w-md flex-col gap-2">
      <div className="text-xs font-medium">
        {translate("resources.projects.contacts.report.title")}
      </div>
      <div role="radiogroup" className="flex flex-col gap-1">
        {reasons.map((r) => (
          <label key={r} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name={`report-${contact.id}`}
              className="mt-1"
              checked={reason === r}
              onChange={() => setReason(r)}
            />
            <span className="min-w-0 break-words">
              {translate(`resources.projects.contacts.report.reasons.${r}`)}
              {values[r] && (
                <span className="text-muted-foreground"> · {values[r]}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {reason && (
        <div className="text-xs text-muted-foreground">
          {translate(`resources.projects.contacts.report.effect_${reason}`)}
        </div>
      )}
      <input
        className="h-8 rounded-md border bg-background px-2 text-xs"
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={translate("resources.projects.contacts.report.note")}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={sending || !reason}
          onClick={send}
        >
          {sending && <Loader2 className="h-3 w-3 animate-spin" />}
          {translate("resources.projects.contacts.report.confirm")}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={close}>
          {translate("resources.projects.contacts.report.cancel")}
        </Button>
      </div>
    </div>
  );
};

/** Coche d'encre (le vert reste au tampon « Sourcé ») : donnée trouvée par une recherche de la semaine. */
const FreshMark = ({ date }: { date: string }) => {
  const translate = useTranslate();
  return (
    <span className="ml-2 inline-flex items-center gap-0.5 text-xs font-medium text-foreground">
      <Check className="size-3.5" aria-hidden />
      {translate("resources.projects.contacts.lookup.fresh", { date })}
    </span>
  );
};

const ContactItem = ({
  contact,
  onChange,
  lookups,
  available,
  prices,
  country,
  ownSide,
  highlighted = false,
  inTeam = false,
  onRequested,
  onReached,
}: {
  contact: ProjectContactLink;
  onChange: () => void;
  lookups?: ContactLookup[];
  available: number | null;
  prices: Prices | null;
  country?: string | null;
  ownSide: boolean;
  highlighted?: boolean;
  // Personne rangée dans l'équipe d'une société : une coordonnée trouvée la fait passer dans les contacts de la fiche.
  inTeam?: boolean;
  onRequested: () => void;
  // Adresse copiée ou profil ouvert : la fiche suivie demande si le message est parti.
  onReached?: (kind: "email" | "linkedin") => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const invited = useInvited();
  const [locale] = useLocaleState();
  const joined = inTeam ? null : joinedFromTeam(contact);
  // Donnée trouvée par une recherche de la semaine : coche à côté, pour la repérer au retour sur la fiche.
  const fresh = (kind: ContactLookup["kind"]) => {
    const date = freshFind(lookups, kind);
    return date ? <FreshMark date={formatDay(date, locale)} /> : null;
  };
  const name = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(" ");
  const email = contact.email_jsonb?.[0]?.email;
  const phone = contact.phone_jsonb?.[0]?.number;
  // Les données lues d'un contact viennent d'une même page : l'avis de marché ou le site de sa société ;
  // une personne trouvée par ses seuls profils publics n'a que le lien de son profil comme source.
  const source = contact.evidence.find(
    (e) => e.method !== "search" && e.method !== "provider",
  );
  const profile = contact.evidence.find((e) => e.field === "linkedin");
  // Preuve de l'adresse affichée : confirmée par son serveur (déduite), écrite sur une page (lue), ou fournie.
  const emailProof = contact.evidence.find(
    (e) =>
      e.field === "email" && e.value.toLowerCase() === email?.toLowerCase(),
  );
  const verified =
    emailProof?.method === "inferred" && emailProof.verification === "valid"
      ? emailProof
      : undefined;
  const written =
    emailProof?.method === "extracted" &&
    emailProof.source_url !== source?.source_url
      ? emailProof
      : undefined;
  // Adresse déduite d'avant la règle du 16/10/2026, non confirmée : signalée comme telle.
  const guessed =
    emailProof?.method === "inferred" && !verified ? emailProof : undefined;
  // Une adresse achetée à un fournisseur (spec § 9, étape 5) l'est aussi.
  const provided = contact.evidence.find(
    (e) => e.field === "email" && e.method === "provider",
  );
  // Coordonnées toujours visibles : la donnée, ou le bouton qui la cherche avec son prix (personnes de son côté).
  const searchable = contact.kind === "person" && ownSide;
  const search = usePersonLookups({
    contact,
    lookups,
    available,
    prices,
    country,
    onRequested,
  });
  const canSearch = (kind: ContactLookup["kind"]) =>
    searchable && search.missing[kind];
  // Adresse probable cachée derrière son bouton, pour qu'on ne la prenne pas pour une adresse trouvée.
  const [probableShown, setProbableShown] = useState(false);
  const probableHidden = !email && !!search.probable && !probableShown;
  // Actions rares repliées : place dans la chaîne, signalement d'une donnée fausse.
  const placeable = !contact.funder_only && contact.editable !== false;
  const reportable = ownSide;
  return (
    <li
      id={`project-contact-${contact.contact_id}`}
      data-anchor={`contact-${contact.contact_id}`}
      className={`py-3 text-sm first:pt-0 last:pb-0 rounded-md transition-shadow duration-500 ${highlighted ? "ring-2 ring-ring ring-offset-4 ring-offset-card" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link
          to={`/contacts/${contact.contact_id}/show`}
          className="font-medium underline decoration-border underline-offset-2 hover:decoration-foreground"
        >
          {name}
        </Link>
        <ContactRelation
          contactId={Number(contact.contact_id)}
          canWrite={ownSide && !invited}
        />
        {contact.funder_only ? (
          <FunderBadge />
        ) : (
          contact.decision_role && <PlaceBadge contact={contact} />
        )}
        {contact.project_mention && (
          <Badge
            variant="secondary"
            title={translate("resources.projects.contacts.on_project.help", {
              word: contact.project_mention,
            })}
          >
            {translate("resources.projects.contacts.on_project.label")}
          </Badge>
        )}
        {contact.kind === "service" && (
          <Badge variant="outline">
            {translate("resources.projects.contacts.kinds.service")}
          </Badge>
        )}
        {joined && (
          <Badge variant="secondary">
            {translate(
              `resources.projects.contacts.team.joined_${joined.field}`,
              {
                date: formatDay(joined.date, locale),
              },
            )}
          </Badge>
        )}
      </div>
      {(contact.title ||
        (contact.company_name && contact.company_name !== name)) && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-2">
          {[
            contact.title,
            contact.company_name !== name ? contact.company_name : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          {contact.company_id != null && contact.company_name !== name && (
            // Déjà dans le bloc Sociétés : retiré de la carte sur téléphone, pour la garder sur une ligne.
            <span className="max-md:hidden">
              <CompanySite
                companyId={contact.company_id}
                domain={contact.company_domain}
                compact
              />
            </span>
          )}
        </div>
      )}
      <div className="mt-1 flex flex-col gap-1">
        {email && (
          <CoordinateRow
            icon={Mail}
            label={translate("resources.projects.contacts.lookup.kinds.email")}
          >
            <div>
              {email}
              <button
                type="button"
                className="text-xs text-muted-foreground underline underline-offset-2 ml-2 hover:text-foreground"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(email);
                  } catch {
                    notify("resources.projects.contacts.copy_failed", {
                      type: "error",
                    });
                    return;
                  }
                  notify("resources.projects.contacts.copied", {
                    type: "info",
                  });
                  onReached?.("email");
                }}
              >
                {translate("resources.projects.contacts.copy_email")}
              </button>
              {fresh("email")}
              {verified && (
                <span className="text-xs text-muted-foreground ml-2">
                  (
                  {translate(
                    "resources.projects.contacts.email_status.verified",
                    {
                      date: formatDay(
                        verified.verified_at ?? verified.collected_at,
                        locale,
                      ),
                    },
                  )}
                  )
                </span>
              )}
              {written && (
                <a
                  href={written.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground ml-2 underline"
                >
                  {translate(
                    "resources.projects.contacts.email_status.published",
                    {
                      date: formatDay(written.collected_at, locale),
                    },
                  )}
                </a>
              )}
              {guessed && (
                <span className="text-xs text-muted-foreground ml-2">
                  {translate(
                    `resources.projects.contacts.guessed.${guessed.verification ?? "unknown"}`,
                  )}
                </span>
              )}
              {provided && (
                <span className="text-xs text-muted-foreground ml-2">
                  {translate(
                    `resources.projects.contacts.provided.${provided.verification === "catch_all" ? "catch_all" : "valid"}`,
                  )}
                </span>
              )}
            </div>
          </CoordinateRow>
        )}
        {!email && search.probable && probableShown && (
          <ProbableEmailRow
            probable={search.probable}
            onCopied={() => onReached?.("email")}
          />
        )}
        {phone && (
          <CoordinateRow
            icon={isMobile(phone, country) ? Smartphone : Phone}
            label={translate("resources.projects.contacts.report.phone")}
          >
            <span>{phone}</span>
            {fresh("mobile")}
          </CoordinateRow>
        )}
        {/* Sans mobile, le standard de la société est un second niveau d'appel (demande du 29/10/2026). */}
        {!(phone && isMobile(phone, country)) &&
          contact.company_phone &&
          digits(contact.company_phone) !== digits(phone) && (
            <CompanyPhone
              number={contact.company_phone}
              method={contact.company_phone_method}
              sourceUrl={contact.company_phone_source_url}
              onContact
            />
          )}
        {contact.linkedin_url && (
          <CoordinateRow
            icon={Linkedin}
            label={translate(
              "resources.projects.contacts.lookup.kinds.linkedin",
            )}
          >
            <span className="min-w-0 flex-1 text-xs text-muted-foreground">
              <a
                href={contact.linkedin_url}
                onClick={() => onReached?.("linkedin")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-foreground underline underline-offset-2"
              >
                {translate("resources.projects.contacts.linkedin")}
              </a>
              {fresh("linkedin")}
              {profile &&
                ` · ${translate(
                  `resources.projects.contacts.${profile.method === "provider" ? "linkedin_provider" : "linkedin_found"}`,
                  {
                    date: formatDay(profile.collected_at, locale),
                  },
                )}`}
            </span>
          </CoordinateRow>
        )}
        {(probableHidden || LOOKUP_BUTTONS.some(canSearch)) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {probableHidden && search.probable && (
              <ProbableEmailButton
                probable={search.probable}
                onShow={() => setProbableShown(true)}
              />
            )}
            {LOOKUP_BUTTONS.filter(canSearch).map((kind) => (
              <LookupButton key={kind} kind={kind} search={search} />
            ))}
          </div>
        )}
      </div>
      {searchable && !!search.blocked.length && (
        <p className="mt-1 text-xs text-inferred-ink">
          {translate("resources.projects.contacts.lookup.short", {
            kinds: search.blocked
              .map((kind) =>
                translate(`resources.projects.contacts.lookup.kinds.${kind}`),
              )
              .join(", "),
            price: Math.min(...search.blocked.map(search.price)),
            available,
          })}
        </p>
      )}
      {inTeam && LOOKUP_KINDS.some(canSearch) && (
        <p className="mt-1 text-xs text-muted-foreground">
          {translate("resources.projects.contacts.lookup.joins_contacts")}
        </p>
      )}
      {source && (
        <a
          href={source.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 block w-fit text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {translate("resources.projects.contacts.source", {
            date: formatDay(source.collected_at, locale),
          })}
        </a>
      )}
      {contact.funder_only && <FunderNote />}
      {(placeable || reportable) && (
        <Fold
          className="mt-1"
          summary={translate(
            `resources.projects.contacts.tools.${placeable && reportable ? "both" : placeable ? "place" : "report"}`,
          )}
        >
          <div className="mt-2 flex flex-col gap-3 border-l border-border pl-3">
            {placeable && (
              <DecisionPlace contact={contact} onChange={onChange} />
            )}
            {reportable && (
              <ReportContact
                contact={contact}
                email={email ?? search.probable?.value}
                phone={phone}
                onDone={onChange}
              />
            )}
          </div>
        </Fold>
      )}
    </li>
  );
};

/** Adresse probable (décision du 29/10/2026) : copiable, mais marquée comme non vérifiable, avec ce sur quoi elle
 * repose ; jamais comptée ni débitée comme un e-mail. */
/** Bouton qui montre l'adresse probable, après un avertissement tant que l'utilisateur ne l'a pas écarté. */
const ProbableEmailButton = ({
  probable,
  onShow,
}: {
  probable: ProbableEmail;
  onShow: () => void;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [silence, setSilence] = useState(false);
  const t = (key: string) =>
    translate(`resources.projects.contacts.probable.${key}`);
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        title={t("button_help")}
        onClick={() =>
          warnBeforeProbable(browserStorage()) ? setOpen(true) : onShow()
        }
      >
        <Mail />
        {t("button")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("warning_title")}</DialogTitle>
            <DialogDescription>
              {t(`warning_${probable.basis}`)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 text-sm">
            <p>{t("warning_server")}</p>
            <p>{t("warning_advice")}</p>
            {probable.known_by_providers && (
              <p className="text-muted-foreground">{t("providers")}</p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={silence}
              onCheckedChange={(checked) => setSilence(checked === true)}
            />
            {t("warning_silence")}
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (silence) skipProbableWarning(browserStorage());
                setOpen(false);
                onShow();
              }}
            >
              {t("warning_show")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ProbableEmailRow = ({
  probable,
  onCopied,
}: {
  probable: ProbableEmail;
  onCopied: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  return (
    <CoordinateRow
      icon={Mail}
      label={translate("resources.projects.contacts.probable.label")}
    >
      {/* Base étroite : sans elle, la phrase d'explication fait passer tout le bloc sous l'icône. */}
      <div className="min-w-0 flex-1 basis-40">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-all text-muted-foreground">
            {probable.value}
          </span>
          <Badge
            variant="outline"
            className="border-inferred text-inferred-ink"
            title={translate("resources.projects.contacts.probable.help")}
          >
            {translate("resources.projects.contacts.probable.label")}
          </Badge>
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(probable.value);
              } catch {
                notify("resources.projects.contacts.copy_failed", {
                  type: "error",
                });
                return;
              }
              notify("resources.projects.contacts.copied", { type: "info" });
              onCopied();
            }}
          >
            {translate("resources.projects.contacts.copy_email")}
          </button>
        </div>
        {probable.known_by_providers && (
          <p className="text-xs text-muted-foreground">
            {translate("resources.projects.contacts.probable.providers")}
          </p>
        )}
      </div>
    </CoordinateRow>
  );
};

/** Place dans la chaîne en tête de carte ; d'où elle vient (outil ou correction) est dit au survol et sous la carte. */
const PlaceBadge = ({ contact }: { contact: ProjectContactLink }) => {
  const translate = useTranslate();
  return (
    <Badge variant="outline" className="font-semibold">
      {translate(
        `resources.projects.contacts.decision.roles.${contact.decision_role}`,
      )}
      {contact.decision_method === "inferred" && (
        <span className="font-normal text-muted-foreground">
          {translate("resources.projects.contacts.decision.short_confidence", {
            confidence: Math.round((contact.decision_confidence ?? 0) * 100),
          })}
          {(contact.decision_confidence ?? 0) < LOW_CONFIDENCE && (
            <span className="font-medium text-inferred-ink">
              {translate("resources.projects.contacts.decision.to_check")}
            </span>
          )}
        </span>
      )}
    </Badge>
  );
};
